import { useEffect, useState } from "react";
import { Route, Switch } from "react-router-dom";
import { CheckCircleIcon, DotsHorizontalIcon, ExclamationCircleIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import AtlasKnowledgeHub from "./AtlasKnowledgeHub";
import AtlasAssetLibrary from "./AtlasAssetLibrary";
import { AtlasKnowledge } from "../interfaces";
import { getData, patchData } from "../backend";
import useSWR from "swr";


function Atlas() {
  const { t } = useTranslation();

  const { data: knowledge, mutate } = useSWR<AtlasKnowledge>("/atlas/knowledge/", getData, {
    revalidateOnFocus: false
  });

  async function changeKnowledge(updatedKnowledge: AtlasKnowledge) {
    setSaveState("HAS_CHANGES");
    await mutate(updatedKnowledge, { revalidate: false });
  }

  async function saveKnowledge() {
    setSaveState("SAVING");

    await patchData("/atlas/knowledge/", knowledge)
      .then(mutate)
      .then(() => setSaveState("SAVED"))
      .catch(() => setSaveState("ERROR"));
  }

  const [saveState, setSaveState] = useState<"INITIAL" | "HAS_CHANGES" | "SAVING" | "SAVED" | "ERROR">("INITIAL");

  useEffect(() => {
    document.title = `${t("Atlas")} | Portant`;
    return (() => { document.title = "Portant" });
  }, [t]);

  // Passive sync indicator — live sync replaces the explicit Save button.
  let syncIndicator = null;
  if (saveState === "HAS_CHANGES") {
    syncIndicator = (
      <div className="flex items-center text-gray-400">
        <button className="btn btn-blue text-sm w-20 py-1" onClick={saveKnowledge}>Save</button>
      </div>
    );
  }
  else if (saveState === "SAVING") {
    syncIndicator = (
      <div className="flex items-center text-gray-400">
        <DotsHorizontalIcon className="h-5 w-5 mr-1" />
        <div className="text-sm font-gilroy font-semibold">{t("Saving")}</div>
      </div>
    );
  }
  else if (saveState === "SAVED") {
    syncIndicator = (
      <div className="flex items-center text-green">
        <CheckCircleIcon className="h-5 w-5 mr-1" />
        <div className="text-sm font-gilroy font-semibold">{t("Saved")}</div>
      </div>
    );
  }
  else if (saveState === "ERROR") {
    syncIndicator = (
      <div className="flex items-center text-red">
        <ExclamationCircleIcon className="h-5 w-5 mr-1" />
        <div className="text-sm font-gilroy font-semibold">{t("Error")}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col border border-gray-200 rounded-lg bg-white w-full h-full min-h-0 pb-4">

      {/* Header — pinned inside the canvas so the sync state stays visible */}
      <div className="flex items-start justify-between px-8 pt-8 flex-shrink-0">
        <div className="flex flex-col">
          <div className="font-gilroy font-semibold text-2xl text-gray-900">
            Portant Context Hub
          </div>
          <div className="text-sm text-gray-400 font-semibold mt-1">
            {t("The organisational knowledge context for personalised AI enrichment.")}
          </div>
        </div>
        <div className="flex items-center h-8 flex-shrink-0 ml-6">
          {syncIndicator}
        </div>
      </div>

      {/* Content — scrolls beneath the pinned header and tabs */}
      <div className="flex flex-col flex-grow min-h-0 overflow-y-auto px-8">
        <Switch>
          <Route path="/context-hub/assets">
            <AtlasAssetLibrary />
          </Route>
          <Route path="/context-hub">
            {!!knowledge
              ? <AtlasKnowledgeHub knowledge={knowledge} onChange={changeKnowledge} />
              : <img src="https://static.portant.co/portant-loading-gray.svg" alt="Loading Logo" className='mx-auto my-24 w-20 h-20' />
            }
          </Route>
        </Switch>
      </div>
    </div>
  );
}

export default Atlas;
